import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';

import { apiUrlGenerator } from '../use-api';
import { showResult } from '../utils';

import type { IValue } from 'components/atoms/base-range-datepicker';
import type {
  IPatientSnapshotSummary,
  TSnapshotSummaryType,
} from 'infrastructure/interfaces';

const getUrl = (guid: string) => `core-2/patients/${guid}/snapshot-summary`;

interface IProps {
  patientGuid: string;
  type: TSnapshotSummaryType | null;
  dates: IValue | null;
}
export const PATIENT_SNAPSHOT_SUMMARY_NO_DATA =
  'No data found for patient snapshot summary';

interface IDataProps {
  patientGuid: string;
  type: TSnapshotSummaryType | null;
  startTime: string | undefined;
  endTime: string | undefined;
}

const getPatientSnapshotSummaryKey = (props: IProps) => [
  'getPatientSnapshotSummary',
  props,
];

const queryFn = async ({ patientGuid, ...props }: IDataProps) => {
  const url = getUrl(patientGuid);

  const { data, error } = await dataService.getList<IPatientSnapshotSummary>(
    apiUrlGenerator(url, props),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useGetPatientSnapshotSummary = ({
  patientGuid,
  type,
  dates,
}: IProps) => {
  const startTime = dates?.[0]?.getTime().toString();
  const endTime = dates?.[1]?.getTime().toString();
  const queryKey = getPatientSnapshotSummaryKey({ patientGuid, type, dates });

  const { data, isLoading, error } = useQuery<
    IPatientSnapshotSummary | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn({ patientGuid, type, startTime, endTime }),
    enabled: !!type,
    staleTime: 60 * 1000, // 1m
  });

  useEffect(() => {
    if (error) {
      if (error === PATIENT_SNAPSHOT_SUMMARY_NO_DATA) return;
      showResult(error);
    }
  }, [error]);

  return {
    patientSnapshotSummary: data,
    isLoading,
  };
};
